import React, { useEffect } from 'react';
import type { EligibilityCheckResponse } from '@features/patient-coverages';
import { useSubjectLanguageVariants } from '@features/user';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { formatDistanceToNow, parseISO } from 'date-fns';

import { useEligibilityBadgeState } from '../hooks/useEligibilityBadgeState';
import { useReadinessAction } from '../hooks/useReadinessAction';
import { ELIG_TOKENS } from '../theme/readinessTokens';
import type { BlockReadiness } from '../types/readiness';
import type { EligibilitySource } from '../types/source';
import { isEligibilityCheckDue } from '../utils/eligibilityCheckState';
import { humanizePinnedAt } from '../utils/humanizePinnedAt';

import { EligibilityAlertsBlock } from './EligibilityAlertsBlock';

// Re-exported so existing call sites that import `EligibilitySource` from
// this module keep working — the type itself lives in types/source.ts to
// avoid a circular import with useReadinessAction.
export type { EligibilitySource };

export interface EligibilitySummaryContentProps {
  patientId?: string;
  encounterId?: string;
  patientName?: string;
  source: EligibilitySource;
  /** Fired after a primary or secondary action runs — used by the popover to
   *  close itself, ignored by inline consumers. */
  onActionFired?: () => void;
  /** Show a "Re-run check" affordance next to the provenance line. Default
   *  `false` — only the drawer opts in; the hover popover stays a peek. */
  showRerun?: boolean;
  /** Suppress the inline compact alerts list. Default `false`. The drawer
   *  opts in (passes `true`) because it renders the full alerts block as
   *  its own section. */
  hideAlerts?: boolean;
  /** Suppress the entire provenance row (timestamp + re-run link). Default
   *  `false`. The drawer opts in while a manual lock is active so the lock
   *  provenance owns the timestamp + re-verify affordance instead. */
  hideProvenance?: boolean;
  /** Suppress the readiness secondary action button. Default `false`. The
   *  detail drawer opts in because its VerificationArea already owns the
   *  collect-state secondary path ("Log collection") via inline form
   *  expansion — without this flag, clicking the summary secondary tries
   *  to open another drawer on top of the current one. */
  hideSecondaryAction?: boolean;
}

/**
 * Shared eligibility summary surface — header + hero + supporting + pin +
 * actions + provenance. Used by EligibilityPopover (wrapped in a floating
 * box) and inline by EditAppointment (rendered as a slide section).
 *
 * Owns its own data fetching via useEligibilityBadgeState so callers only
 * need to pass patient/encounter context. Returns null when the gate is off
 * or the patient is in tutorial mode.
 */
export const EligibilitySummaryContent: React.FC<EligibilitySummaryContentProps> = ({
  patientId,
  encounterId,
  patientName,
  source,
  onActionFired,
  showRerun = false,
  hideAlerts = false,
  hideProvenance = false,
  hideSecondaryAction = false,
}) => {
  const { singularCap } = useSubjectLanguageVariants();
  const { eligibilityChecks } = useEventTracking();
  const {
    isHidden,
    readiness,
    hasCoverage,
    pinnedAppointmentAt,
    eligibilityData,
    runCheck,
    isRunning,
    canRunCheck,
  } = useEligibilityBadgeState({ patientId, encounterId });

  const action = useReadinessAction({
    readiness,
    hasCoverage,
    eligibilityData,
    patientId,
    encounterId,
    patientName,
    runCheck,
    canRunCheck,
    isRunningCheck: isRunning,
    source,
  });

  useEffect(() => {
    if (isHidden || !eligibilityData) return;
    eligibilityChecks.badgeViewed?.({
      patient_id: patientId,
      encounter_id: encounterId,
      source,
    });
  }, [isHidden, eligibilityData, eligibilityChecks, patientId, encounterId, source]);

  if (isHidden) return null;
  // Wait for data — hasCoverage defaults to false before the query resolves,
  // which would briefly render the "No insurance on file" hero for every
  // patient.
  if (!eligibilityData) return null;

  const { hero, supporting } = composeHeader(readiness, hasCoverage, eligibilityData);
  const pinSuffix = humanizePinnedAt(pinnedAppointmentAt);
  const provenance = formatProvenance(eligibilityData?.last_checked_at);

  return (
    <Stack spacing={1.25}>
      <Typography
        variant="overline"
        sx={{
          color: ELIG_TOKENS.inkFaint,
          fontWeight: 600,
          letterSpacing: '0.06em',
          fontSize: 10,
          lineHeight: 1,
        }}
      >
        {singularCap} Eligibility
      </Typography>

      <Box>
        <Typography
          variant="body1"
          sx={{ color: ELIG_TOKENS.ink, fontWeight: 600, lineHeight: 1.3 }}
        >
          {hero}
        </Typography>
        {supporting && (
          <Typography
            variant="body2"
            sx={{ color: ELIG_TOKENS.inkSoft, mt: 0.25, lineHeight: 1.4 }}
          >
            {supporting}
          </Typography>
        )}
        {pinSuffix && (
          <Typography
            variant="caption"
            sx={{ color: ELIG_TOKENS.inkFaint, display: 'block', mt: 0.25 }}
          >
            {capitalize(pinSuffix)}
          </Typography>
        )}
      </Box>

      {!hideAlerts && (
        <EligibilityAlertsBlock
          patientId={patientId}
          encounterId={encounterId}
          variant="compact"
        />
      )}

      {action &&
        (action.primary || (action.secondary && !hideSecondaryAction)) && (
        <Stack direction="row" spacing={1}>
          {action.primary && (
            <Button
              variant="contained"
              size="small"
              disabled={action.primary.disabled}
              onClick={() => {
                action.primary?.onClick();
                onActionFired?.();
              }}
              sx={{
                bgcolor: ELIG_TOKENS.midnight,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.92)' },
                '&:disabled': { bgcolor: ELIG_TOKENS.midnight, opacity: 0.55 },
              }}
            >
              {action.primary.label}
            </Button>
          )}
          {!hideSecondaryAction && action.secondary && (
            <Button
              variant="text"
              size="small"
              disabled={action.secondary.disabled}
              onClick={() => {
                action.secondary?.onClick();
                onActionFired?.();
              }}
              sx={{
                color: ELIG_TOKENS.inkSoft,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {action.secondary.label}
            </Button>
          )}
        </Stack>
      )}

      {!hideProvenance && (provenance || (showRerun && canRunCheck)) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {provenance && (
            <Typography
              variant="caption"
              sx={{ color: ELIG_TOKENS.inkFaint, fontSize: 11 }}
            >
              {provenance}
            </Typography>
          )}
          {showRerun && canRunCheck && (
            <Box
              component="button"
              onClick={runCheck}
              disabled={isRunning}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: isRunning ? 'wait' : 'pointer',
                color: ELIG_TOKENS.inkSoft,
                fontSize: 11,
                textDecoration: 'underline',
                textDecorationThickness: '1px',
                textUnderlineOffset: '2px',
                '&:hover:not(:disabled)': { color: ELIG_TOKENS.midnight },
                '&:disabled': { opacity: 0.6 },
              }}
            >
              {isRunning ? 'Running…' : 'Re-run check'}
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );
};

// ─── Header composition ─────────────────────────────────────────────────────

function composeHeader(
  readiness: BlockReadiness | null,
  hasCoverage: boolean,
  data: EligibilityCheckResponse | undefined,
): { hero: string; supporting: string | null } {
  if (data?.is_self_pay) {
    return {
      hero: 'Self-pay confirmed',
      supporting: 'No eligibility checks needed for this patient.',
    };
  }
  if (!hasCoverage) {
    return { hero: 'No insurance on file', supporting: null };
  }
  if (isEligibilityCheckDue(data)) {
    return {
      hero: 'Eligibility check needed',
      supporting:
        'Coverage is on file, but no recent eligibility check is available for the upcoming visit.',
    };
  }
  if (readiness === null) {
    return {
      hero: 'Eligibility check needed',
      supporting: 'Run a check to verify coverage for this visit.',
    };
  }
  // Prefer the BE's verbose label; fall back to a generic verdict when null.
  const hero = data?.readiness_label?.trim() || readinessFallback(readiness);
  const supporting = pickSupporting(data);
  return { hero, supporting };
}

function readinessFallback(readiness: BlockReadiness): string {
  switch (readiness) {
    case 'verified': return 'Coverage verified';
    case 'collect': return 'Collect copay';
    case 'collected': return 'Copay collected';
    case 'attention': return 'Needs review';
    case 'blocked': return "Don't bill yet";
    case 'info': return 'Coverage info';
  }
}

/**
 * Picks the supporting line. Skips `summary` when it looks enum-shaped (a
 * trust boundary against BE leakage); otherwise renders it verbatim.
 */
function pickSupporting(data: EligibilityCheckResponse | undefined): string | null {
  if (!data) return null;
  const summary = data.summary?.trim();
  if (!summary) return null;
  if (looksLikeEnum(summary)) return null;
  return summary;
}

function looksLikeEnum(s: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(s);
}

function formatProvenance(lastCheckedAt: string | null | undefined): string | null {
  if (!lastCheckedAt) return null;
  try {
    const when = parseISO(lastCheckedAt);
    if (Number.isNaN(when.getTime())) return null;
    return `Checked ${formatDistanceToNow(when, { addSuffix: true })}`;
  } catch {
    return null;
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
